import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Popup } from 'antd-mobile';
import {
  CheckOutline,
  HistogramOutline,
  RightOutline,
  SearchOutline,
  UserOutline
} from 'antd-mobile-icons';
import SafeArea from '@/components/SafeArea';
import { useThemeStore, ThemeMode } from '@/stores/themeStore';
import { useAuthStore } from '@/stores/authStore';
import useBackHandler, { BACK_PRIORITY } from '@/hooks/useBackHandler';
import { BottleIcon, LogoutIcon, MoonIcon } from '@/components/Icon';
import './styles.css';

const themeOptions: Array<{ key: ThemeMode; label: string }> = [
  { key: 'system', label: '跟随系统' },
  { key: 'light', label: '浅色模式' },
  { key: 'dark', label: '深色模式' }
];

interface SettingsRowProps {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  value?: string;
  danger?: boolean;
  onClick?: () => void;
}

function SettingsRow({ icon, title, subtitle, value, danger = false, onClick }: SettingsRowProps) {
  const content = (
    <>
      <span className="settings-row__icon">{icon}</span>
      <span className="settings-row__copy">
        <strong>{title}</strong>
        {subtitle ? <small>{subtitle}</small> : null}
      </span>
      {value ? <span className="settings-row__value">{value}</span> : null}
      {onClick ? <RightOutline className="settings-row__chevron" /> : null}
    </>
  );

  if (!onClick) {
    return <div className={`settings-row${danger ? ' settings-row--danger' : ''}`}>{content}</div>;
  }

  return (
    <button
      type="button"
      className={`settings-row${danger ? ' settings-row--danger' : ''}`}
      onClick={onClick}
      data-haptic={danger ? 'medium' : 'selection'}
    >
      {content}
    </button>
  );
}

const commonItems = [
  {
    title: '数据统计',
    subtitle: '查看销售统计图表',
    icon: <HistogramOutline />,
    path: '/statistics?from=settings'
  },
  {
    title: '全局搜索',
    subtitle: '搜索客户、村和销售记录',
    icon: <SearchOutline />,
    path: '/search'
  },
  {
    title: '酒品分类管理',
    subtitle: '管理白酒分类和价格档位',
    icon: <BottleIcon />,
    path: '/settings/categories'
  }
];

export default function Settings() {
  const navigate = useNavigate();
  const { theme, setTheme } = useThemeStore();
  const { user, clearAuth } = useAuthStore();
  const [themePopupVisible, setThemePopupVisible] = useState(false);

  useBackHandler(
    themePopupVisible,
    () => setThemePopupVisible(false),
    BACK_PRIORITY.popup
  );

  const username = user?.username || 'admin';
  const selectedThemeLabel = themeOptions.find((option) => option.key === theme)?.label || '跟随系统';

  const logout = () => {
    clearAuth();
    window.location.href = '/#/login';
  };

  return (
    <div className="ios-page settings-page">
      <SafeArea className="settings-hero">
        <div className="settings-hero__inner">
          <h1>我的</h1>
          <div className="settings-account">
            <div className="settings-account__mark" aria-hidden="true">醉</div>
            <div className="settings-account__copy">
              <strong>{username}</strong>
              <span>汉州醉经营账户</span>
            </div>
          </div>
        </div>
      </SafeArea>

      <main className="settings-content">
        <section className="settings-section" aria-labelledby="settings-common-title">
          <h2 id="settings-common-title">常用功能</h2>
          <div className="settings-list">
            {commonItems.map((item) => (
              <SettingsRow
                key={item.title}
                icon={item.icon}
                title={item.title}
                subtitle={item.subtitle}
                onClick={() => navigate(item.path)}
              />
            ))}
          </div>
        </section>

        <section className="settings-section" aria-labelledby="settings-account-title">
          <h2 id="settings-account-title">账号</h2>
          <div className="settings-list">
            <SettingsRow icon={<UserOutline />} title="当前用户" value={username} />
            <SettingsRow icon={<LogoutIcon />} title="退出登录" danger onClick={logout} />
          </div>
        </section>

        <section className="settings-section" aria-labelledby="settings-appearance-title">
          <h2 id="settings-appearance-title">外观</h2>
          <div className="settings-list">
            <SettingsRow
              icon={<MoonIcon />}
              title="主题模式"
              value={selectedThemeLabel}
              onClick={() => setThemePopupVisible(true)}
            />
          </div>
        </section>
      </main>

      <Popup
        visible={themePopupVisible}
        position="bottom"
        closeOnSwipe
        closeOnMaskClick
        onMaskClick={() => setThemePopupVisible(false)}
        onClose={() => setThemePopupVisible(false)}
        bodyClassName="settings-theme-popup"
        bodyStyle={{ maxWidth: 430, margin: '0 auto' }}
        maskStyle={{ background: 'rgba(12, 8, 7, 0.68)' }}
      >
        <div className="settings-theme-sheet">
          <div className="settings-theme-sheet__handle" aria-hidden="true" />
          <h2>主题模式</h2>
          <div className="settings-theme-sheet__options">
            {themeOptions.map((option) => {
              const selected = option.key === theme;
              return (
                <button
                  type="button"
                  key={option.key}
                  className="settings-theme-sheet__option"
                  aria-pressed={selected}
                  onClick={() => {
                    setTheme(option.key);
                    setThemePopupVisible(false);
                  }}
                >
                  <span>{option.label}</span>
                  {selected ? <CheckOutline aria-hidden="true" /> : null}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="settings-theme-sheet__cancel"
            onClick={() => setThemePopupVisible(false)}
          >
            取消
          </button>
        </div>
      </Popup>
    </div>
  );
}
